import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { customersKey, machineInfosKey, productsKey, tokenKey, activeOrgKey } from './constant';
import { toast } from 'react-toastify';
import { useClient } from './connect_api';
import { AuthService, OrganizationService, OrganizationInfo, AccessibleOrganization } from './api/api_pb';

interface AuthContextType {
    token: string | null;
    organizationInfo: OrganizationInfo | null;
    accessibleOrganizations: AccessibleOrganization[];
    activeOrganizationUuid: string | null;
    loading: boolean;
    error: string | null;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
    switchOrganization: (orgUuid: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(localStorage.getItem(tokenKey));
    const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo | null>(null);
    const [accessibleOrganizations, setAccessibleOrganizations] = useState<AccessibleOrganization[]>([]);
    const [activeOrganizationUuid, setActiveOrganizationUuid] = useState<string | null>(
        localStorage.getItem(activeOrgKey)
    );
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const authClient = useClient(AuthService);
    const organizationClient = useClient(OrganizationService);

    const fetchOrganizationInfo = useCallback(async (authToken: string) => {
        setLoading(true);
        setError(null);
        try {
          const response = await organizationClient.getMyOrganizationInfo({});
          if (response.organizationInfo) {
            setOrganizationInfo(response.organizationInfo);
          } else {
            setError('No organization data returned');
            toast.error('No organization data found');
          }
        } catch (err) {
          console.error('Failed to fetch organization info:', err);
          setError('Failed to fetch organization data');
          toast.error('Failed to load organization data');
        } finally {
          setLoading(false);
        }
      }, [organizationClient]);

    const fetchAccessibleOrganizations = useCallback(async () => {
        try {
          const response = await authClient.listMyOrganizations({});
          setAccessibleOrganizations(response.organizations);
          if (response.activeOrganizationUuid) {
            setActiveOrganizationUuid(response.activeOrganizationUuid);
            localStorage.setItem(activeOrgKey, response.activeOrganizationUuid);
          }
        } catch (err) {
          console.error('Failed to fetch accessible organizations:', err);
        }
      }, [authClient]);

    useEffect(() => {
        const storedToken = localStorage.getItem(tokenKey);
        if (storedToken) {
          setToken(storedToken);
          fetchOrganizationInfo(storedToken);
          fetchAccessibleOrganizations();
        } else {
          setLoading(false);
        }
      }, [fetchOrganizationInfo, fetchAccessibleOrganizations]);

    const login = async (username: string, password: string) => {
        try {
            const response = await authClient.login({
                email: username,
                password: password,
            });
            localStorage.setItem(tokenKey, response.token);
            setToken(response.token);

            // Store multi-org data from login response
            if (response.accessibleOrganizations.length > 0) {
                setAccessibleOrganizations(response.accessibleOrganizations);
            }
            if (response.activeOrganizationUuid) {
                setActiveOrganizationUuid(response.activeOrganizationUuid);
                localStorage.setItem(activeOrgKey, response.activeOrganizationUuid);
            }

            window.dispatchEvent(new Event('storage'));
            await fetchOrganizationInfo(response.token);
            toast.dismiss();
            toast.success('Login successful!');
        } catch (error) {
            localStorage.removeItem(tokenKey);
            localStorage.removeItem(activeOrgKey);
            setToken(null);
            setOrganizationInfo(null);
            setAccessibleOrganizations([]);
            setActiveOrganizationUuid(null);
            setError('Login failed');
            toast.dismiss();
            toast.error('Login failed. Please check your credentials.');
            throw error;
        }
    };

    const switchOrganization = async (orgUuid: string) => {
        try {
            const response = await authClient.switchOrganization({
                organizationUuid: orgUuid,
            });
            localStorage.setItem(tokenKey, response.token);
            setToken(response.token);
            setActiveOrganizationUuid(response.activeOrganizationUuid);
            localStorage.setItem(activeOrgKey, response.activeOrganizationUuid);
            window.dispatchEvent(new Event('storage'));
            await fetchOrganizationInfo(response.token);
            toast.dismiss();
            toast.success('Organization switched successfully');
        } catch (err) {
            console.error('Failed to switch organization:', err);
            toast.dismiss();
            toast.error('Failed to switch organization. Please try again.');
            throw err;
        }
    };

    const logout = () => {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(activeOrgKey);
        localStorage.removeItem(customersKey);
        localStorage.removeItem(productsKey);
        localStorage.removeItem(machineInfosKey);
        setToken(null);
        setOrganizationInfo(null);
        setAccessibleOrganizations([]);
        setActiveOrganizationUuid(null);
        setError(null);
        toast.dismiss();
        toast.info('Logged out successfully');
        window.location.reload();
    };

    return (
        <AuthContext.Provider value={{
            token,
            organizationInfo,
            accessibleOrganizations,
            activeOrganizationUuid,
            loading,
            error,
            login,
            logout,
            switchOrganization,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
